/**
 * Estate Plan Summary — Pre-Generation Attorney Review
 *
 * Renders an authoritative plain-English summary from the canonical
 * fiduciary model. This summary is computed from the same data that
 * drives document generation, so they cannot diverge.
 *
 * The attorney reviews and confirms this before documents are generated.
 */

import { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import {
  Check as CheckIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { FIDUCIARY_ROLES } from '../config/packageCapabilities.js';

// Role display order
const ROLE_DISPLAY_ORDER = [
  'executor',
  'trustee',
  'trust_protector',
  'estate_protector',
  'guardian',
  'financial_agent',
  'healthcare_agent',
];

// Tier labels by role type
function getTierLabel(roleId, tierIndex) {
  const roleDef = FIDUCIARY_ROLES[roleId];
  if (!roleDef) return `Tier ${tierIndex + 1}`;

  if (roleDef.hasInitialAppointment) {
    if (tierIndex === 0) return 'Primary';
    return tierIndex === 1 ? '1st Successor' : `${tierIndex}${tierIndex === 2 ? 'nd' : 'rd'} Successor`;
  }
  // Trustee, trust protector: all tiers are successors
  return tierIndex === 0 ? '1st Successor' : `${tierIndex + 1}${tierIndex + 1 === 2 ? 'nd' : tierIndex + 1 === 3 ? 'rd' : 'th'} Successor`;
}

const EstatePlanSummary = ({
  canonicalModel,
  renderVariables,
  validationResult,
}) => {
  if (!canonicalModel || !renderVariables) return null;

  const { packageConfig, trustConfig, people, roleAssignments, client, spouse, children } = canonicalModel;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Estate Plan Summary — {packageConfig?.package_type || 'Unknown Package'}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Validation warnings */}
      {validationResult?.warnings?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {validationResult.warnings.map((w, i) => (
            <Alert severity="warning" key={i} sx={{ mb: 1 }} icon={<WarningIcon />}>
              {w.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Client & Family */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">Client</Typography>
        <Typography variant="body1">{client?.full_name || '—'}</Typography>
        {spouse?.full_name && (
          <Typography variant="body2" color="text.secondary">
            Married to {spouse.full_name}
          </Typography>
        )}
        {children?.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            Children: {children.map(c => `${c.full_name}${c.is_minor ? ' (minor)' : ' (adult)'}`).join(', ')}
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Fiduciary Appointments */}
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Fiduciary Appointments
      </Typography>

      {ROLE_DISPLAY_ORDER.map(roleId => {
        const assignment = roleAssignments?.[roleId];
        const tiers = assignment?.tiers || [];
        const roleDef = FIDUCIARY_ROLES[roleId];
        if (!roleDef) return null;

        // Skip guardian if no minor children
        if (roleId === 'guardian' && !packageConfig?.has_minor_children) return null;

        // Skip roles with no assignments and no appointments
        if (tiers.length === 0 && roleId !== 'trustee') return null;

        return (
          <Box key={roleId} sx={{ mb: 1.5, pl: 2 }}>
            <Typography variant="body2" fontWeight="medium" color="text.secondary">
              {roleDef.label}
              {roleId === 'trustee' && ' (client is initial)'}
            </Typography>
            {tiers.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ pl: 2 }}>
                None assigned
              </Typography>
            ) : (
              tiers.map((tier, idx) => {
                const persons = tier.people.map(id => people[id]).filter(Boolean);
                const isPair = persons.length === 2;
                const label = getTierLabel(roleId, idx);

                return (
                  <Box key={idx} sx={{ pl: 2, display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                      {label}:
                    </Typography>
                    <Typography variant="body2">
                      {persons.map(p => p.name).join(' & ')}
                      {persons[0]?.relationship && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}({persons.map(p => p.relationship).filter(Boolean).join(', ')})
                        </Typography>
                      )}
                    </Typography>
                    {isPair && (
                      <Chip
                        icon={<PeopleIcon />}
                        label="Co-fiduciary"
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        );
      })}

      <Divider sx={{ my: 2 }} />

      {/* Trust Configuration */}
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Trust Configuration
      </Typography>
      <Box sx={{ pl: 2 }}>
        <Stack spacing={0.5}>
          <SummaryLine
            label="Minor children"
            value={packageConfig?.has_minor_children
              ? "Yes — Descendants' Single Trust included"
              : "No — Descendants' Single Trust skipped"
            }
          />
          {packageConfig?.has_minor_children && (
            <SummaryLine
              label="Pot trust ends at age"
              value={trustConfig?.pot_trust_termination_age}
            />
          )}
          <SummaryLine
            label="Co-Trustee at age"
            value={trustConfig?.beneficiary_cotrust_age}
          />
          <SummaryLine
            label="Sole Trustee at age"
            value={trustConfig?.beneficiary_sole_trust_age}
          />
          <SummaryLine
            label="Trust Protector at age"
            value={trustConfig?.trust_protector_age}
          />
          {(packageConfig?.package_type?.includes('Disclaimer') ||
            packageConfig?.package_type?.includes('QTIP')) && (
            <SummaryLine
              label="Survivorship"
              value={packageConfig?.survivorship_direction === 'client_survives'
                ? 'Client deemed to survive spouse'
                : 'Spouse deemed to survive client'
              }
            />
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

function SummaryLine({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
        {label}:
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

export default EstatePlanSummary;
